import React, { useEffect, useState } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { Button, Typography } from '@mui/material';
import ResponsiveDialog from '#component/general/responsive-fullscreen-dialog';
import { fetchWarehouse, clear, createWarehouse, updateWarehouse } from '#action/warehouse/warehouse';
import { updateBreadcrumb } from '#action/breadcrumb';
import PreloaderComponent from '#component/preloader';
import Form from './form';
import { useError } from '#hooks';
import { setDocumentTitle } from '#helper';

/**
 * Renders warehouse form.
 * Prepares initial form values.
 * @returns {React.FC}
 */

const WarehouseForm = props => {
  const { match: { params: { id } }, history } = props;
  const dispatch = useDispatch();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [createdWarehouseId, setCreatedWarehouseId] = useState(0);
  const { status, warehouse, error } = useSelector(s => s.warehouse, shallowEqual);
  const loading = id && status === 'loading';
  const pageDescription = id ? 'Edit site' : 'Create site';

  const handleClose = () => {
    setDialogOpen(false);
    history.push('/warehouse');
  };

  const handleOnCreateNewApplicationClick = () => {
    setDialogOpen(false);
    if (createdWarehouseId !== 0) {
      history.push(`/warehouse/appliance/create/${createdWarehouseId}`);
    } else history.push('/warehouse/appliance/create');
  };

  useError(error);

  useEffect(() => {
    if (id) {
      setDocumentTitle('Edit site', warehouse.label);
    } else {
      setDocumentTitle('Create site');
    }

    dispatch(updateBreadcrumb([
      { label: 'Sites', path: '/warehouse' },
      { label: 'Site management', path: '/warehouse-manager' },
      { label: pageDescription }
    ]));
  }, [dispatch, id, pageDescription, warehouse.label]);

  useEffect(() => {
    if (id) {
      dispatch(fetchWarehouse(id, true));
    }

    return () => {
      dispatch(clear());
    };
  }, [dispatch, id]);

  if (loading) {
    return <PreloaderComponent />;
  }

  const initialValues = {
    label: warehouse.label ?? '',
    projectId: warehouse.project?.id ?? null
  };

  const handleFormSubmit = async values => {
    if (id) {
      await dispatch(updateWarehouse(id, values));
      history.push('/warehouse');
    } else {
      const result = await dispatch(createWarehouse(values));
      setCreatedWarehouseId(result.id);
      setDialogOpen(true);
    }
  };

  return (
    <>
      <Form
        {...props}
        initialValues={initialValues}
        onSubmit={handleFormSubmit}
        pageDescription={pageDescription}
        warehouse={warehouse}
      />
      <ResponsiveDialog
        open={dialogOpen}
        onClose={handleClose}
        title="Create application"
        content={
          <p>
            <Typography variant="h6" style={{ marginBottom: 16 }}>
              Do you want to directly create
              a new application that will be assigned to the new site?
            </Typography>
            You can also create an application later, by going to the
            {' '}
            <span style={{ fontWeight: 500, fontSize: '1.1rem' }}>
              Site Manager
            </span>
            {' '}
            on the main page of the sites. If you want to move an existing application to your
            new site, go to the settings of the application and select the new site there.
          </p>
        }
        actions={
          <>
            <Button variant="outlined" onClick={handleClose}>Later</Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleOnCreateNewApplicationClick}
              autoFocus
            >
              Create new application
            </Button>
          </>
        }
      />
    </>
  );
};

export default WarehouseForm;
